import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { Avatar, Chip, createStyles, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';

import { useUpdateTaskMutation } from '../../../../graphql/gen/types';
import { TextSizesEnum } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { NewCollaboratorSearch } from '../../dialog/create_plex/form_components/NewCollaboratorSearch';
import { canEditTask } from '../../permissions';
import { TaskField } from './common/TaskField';

type Props = {
  id: string;
  assignee:
    | {
        id: string;
        name: string;
        picture: string;
      }
    | null
    | undefined;
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type State =
  | { type: 'initial' }
  | {
      type: 'editing';
      value:
        | {
            id: string;
            name: string;
            picture: string;
          }
        | null
        | undefined;
    };

export const TaskAssignee = ({ id, plexRelation, taskRelation, assignee }: Props): React.ReactElement => {
  const classes = useStyles();
  const [state, setState] = useState<State>({ type: 'initial' });
  const [updateTask, { loading }] = useUpdateTaskMutation();

  const currentAssignee = state.type === 'editing' ? state.value : assignee;

  const content = (
    <>
      {currentAssignee != null && (
        <Chip
          avatar={<Avatar alt={currentAssignee.name} src={currentAssignee.picture} />}
          label={currentAssignee.name}
          onDelete={
            state.type === 'editing'
              ? () => {
                  setState({ ...state, value: null });
                }
              : undefined
          }
        />
      )}
      {state.type === 'initial' && (
        <>
          {assignee == null && (
            <PlexTypography className={classes.assigneeContainer} size={TextSizesEnum.Normal}>
              Unassigned
            </PlexTypography>
          )}
        </>
      )}
      {state.type === 'editing' && (
        <>
          {state.value == null && (
            <NewCollaboratorSearch
              onAdd={(collaborator) => {
                if (collaborator.plexUserId != null) {
                  setState({
                    ...state,
                    value: {
                      id: collaborator.plexUserId,
                      name: collaborator.name || '',
                      picture: collaborator.picture || '',
                    },
                  });
                  updateTask({ variables: { task: { id, assigneeId: collaborator.plexUserId } } }).then(() => {
                    setState({ type: 'initial' });
                  });
                } else {
                  throw new Error(`Invalid response ${JSON.stringify(collaborator)}`);
                }
              }}
              existingMembers={[]}
              onlySignedUpUsers
              inputProps={{ autoFocus: true }}
            />
          )}
        </>
      )}
    </>
  );

  switch (state.type) {
    case 'initial':
      return (
        <div className={classes.root}>
          <TaskField
            mode="view"
            label="Assignee"
            onClick={
              canEditTask({ plexRelation, taskRelation })
                ? () => {
                    setState({ type: 'editing', value: assignee });
                  }
                : null
            }
          >
            {content}
          </TaskField>
        </div>
      );
    case 'editing':
      return (
        <div className={classes.root}>
          <TaskField
            mode="edit"
            saving={loading}
            label="Assignee"
            onClickAway={() => {
              if (state.value?.id !== assignee?.id) {
                updateTask({ variables: { task: { id, assigneeId: state.value?.id || null } } }).then(() => {
                  setState({ type: 'initial' });
                });
              } else {
                setState({ type: 'initial' });
              }
            }}
          >
            {content}
          </TaskField>
        </div>
      );
  }
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      height: 44,
      display: 'inline-flex',
      alignItems: 'center',
    },
    assigneeContainer: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  })
);
